import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Check, ArrowLeft, UploadCloud } from "lucide-react";
import { EASE } from "../components/verlo/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK = [
  "Reading your file",
  "Detected 47 menu items",
  "Mapping to 12 categories",
  "Recognising 6 modifier groups",
  "Matching allergens across the range",
  "Bridge is configuring your business",
];

export default function Sandbox() {
  const navigate = useNavigate();
  const [step, setStep] = useState("upload"); // upload | parsing | capture
  const [fileName, setFileName] = useState("");
  const [progress, setProgress] = useState(0);
  const [lines, setLines] = useState([]);
  const [visibleLines, setVisibleLines] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const startParsing = useCallback(async (name) => {
    setFileName(name);
    setStep("parsing");
    setProgress(0);
    setVisibleLines([]);

    let detections = FALLBACK;
    try {
      const res = await axios.post(`${API}/sandbox/detections`, { filename: name });
      if (res.data?.lines?.length) detections = res.data.lines;
    } catch (e) {
      // keep fallback
    }
    setLines(detections);
  }, []);

  // Progress + line reveal animation once we know how many lines there are.
  useEffect(() => {
    if (step !== "parsing" || lines.length === 0) return;
    const total = 4600;
    const start = Date.now();
    const tick = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min(100, Math.round((elapsed / total) * 100));
      setProgress(pct);
      const idx = Math.min(lines.length, Math.floor((pct / 100) * lines.length) + 1);
      setVisibleLines(lines.slice(0, idx));
      if (pct >= 100) {
        clearInterval(tick);
        setTimeout(() => setStep("capture"), 900);
      }
    }, 60);
    return () => clearInterval(tick);
  }, [step, lines]);

  const onFile = (file) => {
    if (!file) return;
    startParsing(file.name);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onFile(file);
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await axios.post(`${API}/waitlist`, { email, source: "sandbox" });
      setSubmitted(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper flex flex-col" data-testid="sandbox-page">
      <header className="px-6 md:px-12 h-20 flex items-center justify-between">
        <button
          data-testid="sandbox-back-home"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-sm text-verlo/60 hover:text-verlo transition-colors"
        >
          <ArrowLeft size={16} /> Back to Verlo
        </button>
        <span className="leading-none">
          <img src="/verlo-logo.svg" alt="Verlo" className="h-5 w-auto" />
        </span>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 md:px-12 py-16">
        <div className="w-full max-w-3xl">
          <AnimatePresence mode="wait">
            {/* STEP 1 : UPLOAD */}
            {step === "upload" && (
              <motion.div
                key="upload"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                transition={{ duration: 0.6, ease: EASE }}
              >
                <p className="text-xs tracking-[0.25em] uppercase text-brass mb-6">
                  The Verlo sandbox
                </p>
                <h1 className="font-display font-light text-4xl md:text-6xl tracking-tight leading-[1.15] text-verlo mb-6">
                  Try Verlo with your own restaurant.
                </h1>
                <p className="text-base md:text-lg text-verlo max-w-xl mb-12">
                  Upload your menu, CSV or product list and watch Verlo configure itself
                  around your business. This is a preview, so nothing is stored or parsed
                  for real just yet.
                </p>

                <div
                  data-testid="sandbox-dropzone"
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={onDrop}
                  onClick={() => inputRef.current?.click()}
                  className={`cursor-pointer border border-dashed rounded-xl px-8 py-16 text-center transition-colors duration-300 ${
                    dragging ? "border-brass bg-brass/5" : "border-verlo/25 hover:border-verlo/50"
                  }`}
                >
                  <UploadCloud className="mx-auto mb-5 text-brass" size={34} />
                  <p className="font-display text-xl text-verlo mb-2">
                    Drop your file here
                  </p>
                  <p className="text-sm text-verlo/50">or click to choose a CSV, menu or product list</p>
                  <input
                    ref={inputRef}
                    data-testid="sandbox-upload-input"
                    type="file"
                    accept=".csv,.txt,.xlsx,.pdf,.json"
                    className="hidden"
                    onChange={(e) => onFile(e.target.files?.[0])}
                  />
                </div>

                <button
                  data-testid="sandbox-demo-file"
                  onClick={() => startParsing("sample-menu.csv")}
                  className="mt-6 text-sm text-verlo/50 hover:text-brass transition-colors underline underline-offset-4"
                >
                  No file to hand? Use a sample menu
                </button>
              </motion.div>
            )}

            {/* STEP 2 : PARSING */}
            {step === "parsing" && (
              <motion.div
                key="parsing"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                transition={{ duration: 0.6, ease: EASE }}
                data-testid="sandbox-parsing"
              >
                <p className="text-xs tracking-[0.25em] uppercase text-brass mb-6">
                  Bridge is working
                </p>
                <h2 className="font-display font-light text-3xl md:text-5xl tracking-tight text-verlo mb-3">
                  Reading {fileName || "your file"}
                </h2>

                <div className="mt-10 mb-4 flex items-center justify-between text-sm text-verlo/50">
                  <span>Configuring your business</span>
                  <span data-testid="sandbox-progress-value">{progress}%</span>
                </div>
                <div
                  data-testid="sandbox-mock-progress"
                  className="h-[3px] w-full bg-verlo/10 overflow-hidden rounded-full"
                >
                  <motion.div
                    className="h-full bg-brass"
                    animate={{ width: `${progress}%` }}
                    transition={{ ease: "linear", duration: 0.1 }}
                  />
                </div>

                <ul className="mt-12 space-y-4" data-testid="sandbox-detections">
                  {visibleLines.map((line, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, ease: EASE }}
                      className="flex items-center gap-4 text-lg text-verlo"
                    >
                      <span className="text-brass">
                        <Check size={18} />
                      </span>
                      {line}
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            )}

            {/* STEP 3 : EMAIL CAPTURE */}
            {step === "capture" && (
              <motion.div
                key="capture"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                transition={{ duration: 0.6, ease: EASE }}
                data-testid="sandbox-capture"
              >
                {submitted ? (
                  <div data-testid="sandbox-waitlist-success">
                    <p className="text-xs tracking-[0.25em] uppercase text-brass mb-6">
                      You&apos;re on the list
                    </p>
                    <h2 className="font-display font-light text-4xl md:text-6xl tracking-tight leading-[1.15] text-verlo mb-6">
                      We&apos;ll email you the moment your sandbox is ready.
                    </h2>
                    <p className="text-base md:text-lg text-verlo max-w-lg">
                      Thank you. In the meantime, feel free to read the rest of the Verlo
                      story.
                    </p>
                    <button
                      data-testid="sandbox-return-home"
                      onClick={() => navigate("/")}
                      className="mt-10 inline-flex items-center bg-brass text-paper font-medium px-7 py-3.5 rounded-full hover:bg-verlo transition-colors"
                    >
                      Back to the story
                    </button>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs tracking-[0.25em] uppercase text-brass mb-6">
                      Your sandbox is nearly ready
                    </p>
                    <h2 className="font-display font-light text-4xl md:text-6xl tracking-tight leading-[1.15] text-verlo mb-6">
                      That took seconds. The real thing is close.
                    </h2>
                    <p className="text-base md:text-lg text-verlo max-w-lg mb-10">
                      Leave your email and we&apos;ll open your live sandbox the moment it is
                      ready, built from a menu just like the one you dropped in.
                    </p>

                    <form
                      onSubmit={submitEmail}
                      className="flex flex-col sm:flex-row gap-4 max-w-xl"
                    >
                      <input
                        data-testid="sandbox-email-capture"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@yourvenue.co.uk"
                        className="flex-1 bg-transparent border-b border-verlo/30 focus:border-brass outline-none py-3 text-verlo placeholder:text-verlo/40 transition-colors"
                      />
                      <button
                        data-testid="waitlist-submit"
                        type="submit"
                        disabled={busy}
                        className="bg-brass text-paper font-medium px-7 py-3.5 rounded-full hover:bg-verlo transition-colors disabled:opacity-50 whitespace-nowrap"
                      >
                        {busy ? "Adding you" : "Join the list"}
                      </button>
                    </form>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
